import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  SimpleButton,
  LoadingButton,
  Input,
  TextArea,
  FormDialog,
  Spinner,
  useStateContext,
} from "@veda/dashboard-ui";
import { BsPlusLg, BsCheckCircle } from "react-icons/bs";
import { usePppApi } from "../services/pppApi";
import { useAuthUser } from "../hooks/useAuthUser";

const STATO_CFG = {
  bozza:        { cls: "bg-gray-100 text-gray-600" },
  in_revisione: { cls: "bg-yellow-100 text-yellow-700" },
  approvato:    { cls: "bg-green-100 text-green-700" },
};

function StatoBadge({ stato, _locate }) {
  const cfg = STATO_CFG[stato] ?? { cls: "" };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${cfg.cls}`}>
      {_locate(`ppp_schema_stato_${stato}`) ?? stato}
    </span>
  );
}

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("it-IT");
}

export default function SchemaValutazioneList({ procedimentoId }) {
  const navigate = useNavigate();
  const { _locate, addToast, setOpenForm } = useStateContext();
  const api = usePppApi();
  const { can } = useAuthUser();
  const canCrea = can('ppp.schema_valutazione.create');

  const [schemi, setSchemi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creando, setCreando] = useState(false);
  const [nuovoTitolo, setNuovoTitolo] = useState("");
  const [nuovoDescrizione, setNuovoDescrizione] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api.listSchemiValutazione(procedimentoId, {
      onSuccess: (data) => { setSchemi(data); setLoading(false); },
      onFormError: () => setLoading(false),
    });
  }, [procedimentoId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  function handleCrea() {
    if (!nuovoTitolo.trim()) return;
    setCreando(true);
    api.creaSchemaValutazione(procedimentoId, { titolo: nuovoTitolo, descrizione: nuovoDescrizione }, {
      onSuccess: (data) => {
        setCreando(false);
        setOpenForm(false);
        setNuovoTitolo("");
        setNuovoDescrizione("");
        addToast({ title: _locate("ppp_schema_creato"), type: "success" });
        navigate(`/ppp/${procedimentoId}/schemi-valutazione/${data.id}`);
      },
      onError: () => {
        setCreando(false);
        addToast({ title: _locate("ppp_errore_generico"), type: "error" });
      },
    });
  }

  if (loading) return <Spinner />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{_locate("ppp_schema_lista_descrizione")}</p>
        </div>
        {canCrea && (
          <SimpleButton
            text={<><BsPlusLg className="inline mr-1" />{_locate("ppp_schema_nuovo_btn")}</>}
            customFunc={() => setOpenForm(true)}
          />
        )}
      </div>

      {schemi.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-xl">
          <p className="text-gray-400 text-sm">{_locate("ppp_schema_nessuno")}</p>
          <p className="text-gray-400 text-xs mt-1">{_locate("ppp_schema_nessuno_hint")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {schemi.map((schema) => (
            <div
              key={schema.id}
              className="flex items-center gap-4 p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:border-blue-300 cursor-pointer transition-colors"
              onClick={() => navigate(`/ppp/${procedimentoId}/schemi-valutazione/${schema.id}`)}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900 dark:text-white truncate">{schema.titolo}</span>
                  <StatoBadge stato={schema.stato} _locate={_locate} />
                  {schema.stato === "approvato" && <BsCheckCircle className="text-green-500" size={14} />}
                </div>
                <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                  <span>{schema.numeroCriteri} {_locate("ppp_schema_criteri_n")}</span>
                  {schema.totalePeso != null && (
                    <span className={schema.totalePeso === 100 ? "text-green-600" : "text-yellow-600"}>
                      {schema.totalePeso}% peso totale
                    </span>
                  )}
                  {schema.approvatoIl && (
                    <span>{_locate("ppp_schema_approvato_il")} {formatDate(schema.approvatoIl)}</span>
                  )}
                  <span>{_locate("ppp_schema_creato_il")} {formatDate(schema.createdAt)}</span>
                </div>
              </div>
              <span className="text-gray-400 text-sm">›</span>
            </div>
          ))}
        </div>
      )}

      <FormDialog title={_locate("ppp_schema_nuovo_titolo")} size="w-[540px]">
        <div className="space-y-3">
          <Input
            label={_locate("ppp_schema_titolo")}
            value={nuovoTitolo}
            onChange={e => setNuovoTitolo(e.target.value)}
            placeholder={_locate("ppp_schema_titolo_placeholder")}
          />
          <TextArea
            label={_locate("ppp_schema_descrizione")}
            value={nuovoDescrizione}
            onChange={e => setNuovoDescrizione(e.target.value)}
            rows={3}
            placeholder={_locate("ppp_schema_descrizione_placeholder")}
          />
          <div className="flex justify-end gap-2 pt-2">
            <SimpleButton customFunc={() => setOpenForm(false)} text={_locate("ppp_form_annulla")} />
            <LoadingButton isLoading={creando} customFunc={handleCrea} text={_locate("ppp_schema_crea_btn")} />
          </div>
        </div>
      </FormDialog>
    </div>
  );
}
